import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { MapPin, Navigation, Clock, Home, Activity } from 'lucide-react';

interface LocationData {
  time: string;
  location: string;
  duration: number;
  activity: 'ACTIVE' | 'RESTING' | 'SLEEPING' | 'OTHER';
}

interface RoomMetrics {
  name: string;
  timeSpent: number;
  visits: number;
  averageDuration: number;
  commonActivities: string[];
}

const LocationTracking: React.FC = () => {
  // Mock data - in real app this would come from floor sensors
  const recentLocations: LocationData[] = [
    { time: '7:00 AM', location: 'Bedroom', duration: 30, activity: 'ACTIVE' },
    { time: '7:30 AM', location: 'Bathroom', duration: 15, activity: 'ACTIVE' },
    { time: '7:45 AM', location: 'Kitchen', duration: 25, activity: 'ACTIVE' },
    { time: '8:10 AM', location: 'Living Room', duration: 120, activity: 'RESTING' },
    { time: '10:10 AM', location: 'Kitchen', duration: 20, activity: 'ACTIVE' },
    { time: '10:30 AM', location: 'Bathroom', duration: 10, activity: 'ACTIVE' },
    { time: '10:40 AM', location: 'Living Room', duration: 140, activity: 'ACTIVE' },
    { time: '1:00 PM', location: 'Kitchen', duration: 30, activity: 'ACTIVE' },
    { time: '1:30 PM', location: 'Living Room', duration: 90, activity: 'RESTING' }
  ];

  const roomMetrics: RoomMetrics[] = [
    {
      name: 'Living Room',
      timeSpent: 350,
      visits: 3,
      averageDuration: 117,
      commonActivities: ['Watching TV', 'Reading', 'Resting']
    },
    {
      name: 'Kitchen',
      timeSpent: 75,
      visits: 3,
      averageDuration: 25,
      commonActivities: ['Meal Preparation', 'Eating', 'Taking Medication']
    },
    {
      name: 'Bedroom',
      timeSpent: 30,
      visits: 1,
      averageDuration: 30,
      commonActivities: ['Dressing', 'Morning Routine']
    },
    {
      name: 'Bathroom',
      timeSpent: 25,
      visits: 2,
      averageDuration: 12.5,
      commonActivities: ['Personal Care', 'Hygiene']
    }
  ];

  const hourlyActivity = [
    { hour: '6 AM', bedroom: 60, bathroom: 0, kitchen: 0, livingRoom: 0 },
    { hour: '7 AM', bedroom: 30, bathroom: 15, kitchen: 15, livingRoom: 0 },
    { hour: '8 AM', bedroom: 0, bathroom: 0, kitchen: 10, livingRoom: 50 },
    { hour: '9 AM', bedroom: 0, bathroom: 0, kitchen: 0, livingRoom: 60 },
    { hour: '10 AM', bedroom: 0, bathroom: 10, kitchen: 20, livingRoom: 30 },
    { hour: '11 AM', bedroom: 0, bathroom: 0, kitchen: 0, livingRoom: 60 },
    { hour: '12 PM', bedroom: 0, bathroom: 0, kitchen: 0, livingRoom: 60 },
    { hour: '1 PM', bedroom: 0, bathroom: 0, kitchen: 30, livingRoom: 30 }
  ];

  const getActivityIcon = (activity: string) => {
    switch (activity) {
      case 'ACTIVE':
        return <Activity className="text-green-500" />;
      case 'RESTING':
        return <Clock className="text-blue-500" />;
      case 'SLEEPING':
        return <Clock className="text-purple-500" />;
      default:
        return <Activity className="text-gray-500" />;
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Location Tracking</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {roomMetrics.map((room, index) => (
          <div key={index} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-2">
              <Home className="text-blue-500 mr-2" />
              <h3 className="text-lg font-semibold">{room.name}</h3>
            </div>
            <div className="space-y-2">
              <p className="text-3xl font-bold">{room.timeSpent}min</p>
              <p className="text-sm text-gray-500">
                {room.visits} visits • Avg {room.averageDuration}min
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Movement Timeline</h3>
          <div className="space-y-4">
            {recentLocations.map((location, index) => (
              <div key={index} className="flex items-start space-x-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  {getActivityIcon(location.activity)}
                </div>
                <div className="flex-grow">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{location.time}</span>
                    <span className="text-sm text-gray-500">
                      {location.duration} minutes
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {location.location} • {location.activity.toLowerCase()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Room Usage Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={hourlyActivity}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="bedroom" name="Bedroom" fill="#8b5cf6" />
              <Bar dataKey="bathroom" name="Bathroom" fill="#3b82f6" />
              <Bar dataKey="kitchen" name="Kitchen" fill="#10b981" />
              <Bar dataKey="livingRoom" name="Living Room" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Location Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <Activity className="text-green-500" />
              </div>
              <div>
                <h4 className="font-medium">Activity Patterns</h4>
                <p className="text-gray-600">Most active periods observed in the morning and early afternoon.</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <Clock className="text-blue-500" />
              </div>
              <div>
                <h4 className="font-medium">Time Distribution</h4>
                <p className="text-gray-600">Majority of time spent in living room, indicating good mobility and engagement.</p>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <Navigation className="text-purple-500" />
              </div>
              <div>
                <h4 className="font-medium">Navigation Patterns</h4>
                <p className="text-gray-600">Consistent movement patterns suggest good spatial awareness and orientation.</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <MapPin className="text-yellow-500" />
              </div>
              <div>
                <h4 className="font-medium">Room Usage</h4>
                <p className="text-gray-600">Regular visits to all rooms indicate normal daily living activities.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationTracking; 